import { alpha, Box, Typography } from "@mui/material";
import { EffortStats } from "../../types";
import { roundMinutesToNearestHalf } from "~/lib/date";
import { useColorMode } from "~/providers/color-mode";
import { motion } from "motion/react";
import {
  ANIMATION_DELAYS,
  ANIMATION_DURATIONS,
} from "~/pages/integrations/security-pipeline/animation";
import AnimatedNumber from "~/components/AnimatedNumber";
import { RefObject } from "react";

interface RemediationEffortCardProps {
  mondooEffort: EffortStats;
  manualEffort: EffortStats;
  onReplay: () => void;
  showParticles: boolean;
  animationKey: number;
  numberRef: RefObject<HTMLDivElement | null>;
}

export const RemediationWithMondooCard = ({
  mondooEffort,
  manualEffort,
  onReplay,
  showParticles,
  animationKey,
  numberRef,
}: RemediationEffortCardProps) => {
  const { mode } = useColorMode();
  const isLight = mode === "light";
  const { total: mondooTotal } = mondooEffort;
  const { total: manualTotal } = manualEffort;

  const totalMondooEffortInHours = roundMinutesToNearestHalf(mondooTotal);
  const totalManualEffortInHours = roundMinutesToNearestHalf(manualTotal);
  const fasterPercentage =
    ((totalManualEffortInHours - totalMondooEffortInHours) /
      totalManualEffortInHours) *
    100;

  const categories = [
    { key: "Research & Planning", value: "Zilch", italics: true },
    { key: "Coding & Testing", value: "Zero", italics: true },
    { key: "Verification", value: "Nada", italics: true },
    {
      key: "Staged Rollout",
      value: totalMondooEffortInHours,
      italics: false,
    },
  ];

  return (
    <Box
      component={motion.div}
      key={animationKey}
      initial={{ opacity: 0, y: 30, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        duration: ANIMATION_DURATIONS.box,
        delay: ANIMATION_DELAYS.purpleBox,
        type: "spring",
        stiffness: 150,
        damping: 18,
        bounce: 0.1,
      }}
      sx={{
        position: "relative",
        p: 3,
        backgroundColor: (theme) =>
          isLight ? alpha(theme.palette.primary.main, 0.1) : "primary.main",
        borderRadius: 1.5,
      }}
    >
      <Box
        key={`badge-${animationKey}`}
        component={motion.div}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{
          opacity: showParticles ? 1 : 0,
          scale: showParticles ? 1 : 0.8,
        }}
        transition={{
          duration: 0.4,
          delay: showParticles ? ANIMATION_DELAYS.badge : 0,
          type: "spring",
          stiffness: 200,
        }}
        onClick={onReplay}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        sx={{
          position: "absolute",
          top: "4px",
          right: "4px",
          borderRadius: "4px",
          border: (theme) => `1px solid ${theme.palette.primary.darker}`,
          px: "6px",
          cursor: "pointer",
        }}
      >
        <Typography fontWeight={600} fontSize={10} color="primary.darker">
          <AnimatedNumber
            value={fasterPercentage}
            delay={showParticles ? ANIMATION_DELAYS.badge : 0}
            suffix="% Faster"
            animationKey={animationKey}
          />
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          alignSelf: "stretch",
          mt: 0.25,
          mb: 1.5,
        }}
      >
        <Typography
          ref={numberRef}
          color="primary.darker"
          fontSize={60}
          lineHeight="64px"
          fontWeight={900}
          textAlign="center"
          letterSpacing="-1.5px"
        >
          <AnimatedNumber
            value={totalMondooEffortInHours}
            delay={ANIMATION_DELAYS.purpleCounter}
            duration={ANIMATION_DURATIONS.counter}
            animationKey={animationKey}
            isDecimal
          />
        </Typography>
        <Typography
          textAlign="center"
          color="primary.darker"
          fontSize={10}
          fontWeight={600}
        >
          hours with Mondoo
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: 0.5,
          px: 0,
          py: 0.5,
          alignSelf: "stretch",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 0.25,
            alignSelf: "stretch",
          }}
        >
          <Typography
            sx={{
              display: "flex",
              height: 10,
              flexDirection: "column",
              justifyContent: "center",
              flex: "1 0 0",
              color: "primary.darker",
              fontSize: 10,
              lineHeight: "10px",
              letterSpacing: ".4px",
              textTransform: "uppercase",
              opacity: 0.5,
            }}
          >
            task
          </Typography>
          <Typography
            sx={{
              color: "primary.darker",
              fontSize: 10,
              lineHeight: "10px",
              letterSpacing: ".4px",
              textTransform: "uppercase",
              opacity: 0.5,
            }}
          >
            hours
          </Typography>
        </Box>
        {categories.map((c) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 0.25,
              alignSelf: "stretch",
            }}
          >
            <Typography
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                flex: "1 0 0",
                color: "primary.darker",
                fontSize: 10,
                fontWeight: 500,
                lineHeight: "12px",
                letterSpacing: ".4px",
              }}
            >
              {c.key}
            </Typography>
            <Typography
              sx={{
                color: "primary.darker",
                textAlign: "right",
                fontSize: 10,
                fontStyle: c.italics ? "italic" : "normal",
                fontWeight: 700,
                lineHeight: "12px",
                letterSpacing: ".4px",
              }}
            >
              {c.italics ? (
                c.value
              ) : (
                <AnimatedNumber
                  value={c.value as number}
                  delay={c.italics ? 0 : 1.2}
                  duration={ANIMATION_DURATIONS.counter}
                  isDecimal={!c.italics}
                  animationKey={animationKey}
                />
              )}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
